/**
 * NOS-158: in-builder theme switcher. Selecting a card live-previews the
 * theme's tokens + variants on the canvas (nothing persists); Apply commits
 * through the store (undoable), Cancel/close restores the canvas exactly.
 */
import { useEffect, useState } from "react"
import { toast } from "sonner"
import { useCustomizationStore } from "@/stores/customizationStore"
import { getBuiltinGalleryThemes, getGalleryThemes, type GalleryTheme } from "@/lib/themeCatalog"
import { ThemeGallery } from "./ThemeGallery"

export interface ThemeGalleryOverlayProps {
  open: boolean
  onClose: () => void
}

export function ThemeGalleryOverlay({ open, onClose }: ThemeGalleryOverlayProps) {
  const previewTheme = useCustomizationStore((s) => s.previewTheme)
  const cancelThemePreview = useCustomizationStore((s) => s.cancelThemePreview)
  const commitTheme = useCustomizationStore((s) => s.commitTheme)

  const [themes, setThemes] = useState<GalleryTheme[]>(() => getBuiltinGalleryThemes())
  const [selected, setSelected] = useState<GalleryTheme | null>(null)
  const [applying, setApplying] = useState(false)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    getGalleryThemes().then((list) => {
      if (!cancelled) setThemes(list)
    })
    return () => {
      cancelled = true
    }
  }, [open])

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") handleClose()
    }
    if (open) document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  if (!open) return null

  const handleSelect = (theme: GalleryTheme) => {
    setSelected(theme)
    previewTheme(theme.package)
  }

  const handleClose = () => {
    cancelThemePreview()
    setSelected(null)
    onClose()
  }

  const handleApply = async () => {
    if (!selected || applying) return
    setApplying(true)
    try {
      await commitTheme(selected.package, selected.templateId)
      toast.success(`${selected.name} applied — your content is unchanged`)
      setSelected(null)
      onClose()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not apply the theme")
    } finally {
      setApplying(false)
    }
  }

  return (
    <div
      data-testid="theme-gallery-overlay"
      onClick={handleClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1200,
        background: "rgba(0,0,0,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(420px, calc(100vw - 32px))",
          height: "calc(100vh - 32px)",
          margin: 16,
          display: "flex",
          flexDirection: "column",
          background: "rgba(12,12,16,0.98)",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: 14,
          boxShadow: "0 24px 60px rgba(0,0,0,0.5)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "14px 16px",
            borderBottom: "1px solid rgba(255,255,255,0.06)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>Themes</div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", marginTop: 2 }}>
              Click a theme to preview it on your site
            </div>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={handleClose}
            style={{
              border: "none",
              background: "transparent",
              color: "rgba(255,255,255,0.5)",
              fontSize: 15,
              cursor: "pointer",
              padding: 6,
            }}
          >
            ✕
          </button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: 14 }}>
          <ThemeGallery themes={themes} selectedId={selected?.id ?? null} onSelect={handleSelect} mode="dark" />
        </div>

        <div
          style={{
            padding: 14,
            borderTop: "1px solid rgba(255,255,255,0.06)",
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          {selected && (
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", lineHeight: 1.5 }}>
              Your content stays. Colors, fonts, and layouts change.
            </div>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              data-testid="theme-apply-btn"
              onClick={() => void handleApply()}
              disabled={!selected || applying}
              style={{
                flex: 1,
                padding: "10px 0",
                borderRadius: 8,
                border: "none",
                background: selected ? "#fff" : "rgba(255,255,255,0.08)",
                color: selected ? "#0f0f14" : "rgba(255,255,255,0.35)",
                fontSize: 13,
                fontWeight: 700,
                cursor: selected && !applying ? "pointer" : "default",
              }}
            >
              {applying ? "Applying…" : selected ? `Apply ${selected.name}` : "Select a theme"}
            </button>
            <button
              type="button"
              data-testid="theme-cancel-btn"
              onClick={handleClose}
              style={{
                padding: "10px 16px",
                borderRadius: 8,
                border: "1px solid rgba(255,255,255,0.1)",
                background: "transparent",
                color: "rgba(255,255,255,0.6)",
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
